import { z } from "zod";

// ── Unit-level schema ─────────────────────────────────────────────────────────

export const unitFormSchema = z.object({
  purpose: z.string().default("residential"),
  unitType: z.string().min(1, "Unit type required"),
  unitName: z.string().min(1, "Unit name required"),
  unitArea: z.string().optional(),
  unitImages: z.array(z.string()).default([]),
  annualRent: z.string().optional(),
  deposit: z.string().optional(),
  saleAmount: z.string().optional(),
  floorNumber: z.string().optional(),
  isMezzanine: z.boolean().default(false),
  frontSideLength: z.string().optional(),
  finishing: z.string().default("finished"),
  furnishingStatus: z.string().default("unfurnished"),
  bedrooms: z.number().int().min(0).default(1),
  bathrooms: z.number().int().min(0).default(1),
  majles: z.number().int().min(0).default(1),
  kitchens: z.number().int().min(0).default(1),
  maidRooms: z.number().int().min(0).default(0),
  storageRooms: z.number().int().min(0).default(0),
  outdoorAreas: z.number().int().min(0).default(0),
  utilities: z
    .object({
      electricity: z.string().default(""),
      water: z.string().default(""),
      gas: z.string().default(""),
    })
    .default({ electricity: "", water: "", gas: "" }),
  highlights: z
    .object({
      counters: z.record(z.string(), z.number()).default({}),
      checkboxes: z.record(z.string(), z.boolean()).default({}),
    })
    .default({ counters: {}, checkboxes: {} }),
  legal: z
    .object({
      ownershipType: z.string().optional(),
      ownershipNumber: z.string().optional(),
      ownershipDate: z.string().optional(),
      infoRestriction: z.string().nullable().optional(),
      mortgageRestriction: z.string().nullable().optional(),
      obligations: z.string().nullable().optional(),
      propertyDispute: z.string().nullable().optional(),
      ownershipDocuments: z.array(z.string()).default([]),
    })
    .optional(),
  ownerId: z.string().nullable().optional(),
});

export type UnitFormValues = z.infer<typeof unitFormSchema>;

// ── Property-level schema ─────────────────────────────────────────────────────

export const propertyFormSchema = z.object({
  // Required
  purpose: z.string().min(1, "Purpose required"),
  propertyType: z.string().min(1, "Property type required"),
  name: z.string().min(1, "Property name required"),
  ownershipType: z.enum(["single", "multiple"]).default("single"),
  // Optional basic
  nameEn: z.string().optional(),
  ownerId: z.string().nullable().optional(),
  // Public page settings (rendered on /p/:id)
  description: z.string().optional(),
  descriptionEn: z.string().optional(),
  publicUnitsSource: z.enum(["listings", "vacant_units"]).default("listings"),
  area: z.string().optional().refine((v) => !v || (!isNaN(Number(v)) && Number(v) >= 0), "Must be a valid positive number"),
  // Location (top-level fields)
  address: z.string().min(1, "Address required"),
  city: z.string().optional(),
  district: z.string().optional(),
  postalCode: z.string().optional(),
  // Optional gated-compound metadata. Empty strings are treated as "no
  // compound" downstream; the AddPropertyDialog mapper coerces to null.
  compoundName: z.string().optional(),
  compoundCode: z.string().optional(),
  latitude: z.number().nullable().optional(),
  longitude: z.number().nullable().optional(),
  // Images
  images: z.array(z.string()).default([]),
  floorPlanImages: z.array(z.string()).default([]),
  // Sub-tab: information — matches AddPropertyDialog information state fields exactly
  information: z
    .object({
      shortAddress: z.string().optional(),
      nationalAddress: z.string().optional(),
      country: z.string().optional(),
      region: z.string().optional(),
      streetName: z.string().optional(),
      buildingNumber: z.string().optional(),
      secondaryNumber: z.string().optional(),
      streetWidth: z.string().optional(),
      landNumber: z.string().optional(),
      planNumber: z.string().optional(),
      propertyAge: z.string().optional(),
      direction: z.string().optional(),
      propertyValue: z.string().optional(),
      communityName: z.string().optional(),
      floorsCount: z.number().int().min(0).default(1),
      unitsPerFloor: z.number().int().min(0).default(1),
      parkingLots: z.number().int().min(0).default(1),
      elevators: z.number().int().min(0).default(1),
    })
    .default({ floorsCount: 1, unitsPerFloor: 1, parkingLots: 1, elevators: 1 }),
  // Sub-tab: utilities (property-level — array of selected utility IDs)
  utilities: z.array(z.string()).default([]),
  // Sub-tab: highlights (property-level — record of category → selected item IDs)
  highlights: z
    .record(
      z.string(),
      z.array(z.string())
    )
    .default({}),
  // Sub-tab: legal
  legal: z.object({
    ownershipType: z.string().min(1, "Ownership type required"),
    ownershipNumber: z.string().min(1, "Ownership number required"),
    ownershipDate: z.string().min(1, "Ownership date required"),
    infoRestriction: z.string().nullable().optional(),
    mortgageRestriction: z.string().nullable().optional(),
    obligations: z.string().nullable().optional(),
    propertyDispute: z.string().nullable().optional(),
    ownershipDocuments: z.array(z.string()).default([]),
  }),
  // Units (create mode — empty in edit mode)
  units: z.array(unitFormSchema).default([]),
  // Unit-tab current-unit fields (before it is appended to `units`)
  unitPurpose: z.string().optional(),
  unitType: z.string().optional(),
  unitName: z.string().optional(),
  unitArea: z.string().optional(),
  unitImages: z.array(z.string()).default([]),
  annualRent: z.string().optional(),
  deposit: z.string().optional(),
  saleAmount: z.string().optional(),
  floorNumber: z.string().optional(),
  isMezzanine: z.boolean().default(false),
  frontSideLength: z.string().optional(),
  finishing: z.string().default("finished"),
  furnishingStatus: z.string().default("unfurnished"),
  bedrooms: z.number().int().min(0).default(1),
  bathrooms: z.number().int().min(0).default(1),
  majles: z.number().int().min(0).default(1),
  kitchens: z.number().int().min(0).default(1),
  maidRooms: z.number().int().min(0).default(0),
  storageRooms: z.number().int().min(0).default(0),
  outdoorAreas: z.number().int().min(0).default(0),
  unitElectricityAccount: z.string().optional(),
  unitWaterAccount: z.string().optional(),
  unitGasMeter: z.string().optional(),
  unitCentralAC: z.number().int().min(0).default(0),
  unitSplitAC: z.number().int().min(0).default(0),
  unitDesertCooler: z.number().int().min(0).default(0),
  unitWindowAC: z.number().int().min(0).default(0),
  unitParkingCount: z.number().int().min(0).default(0),
  unitHasHeating: z.boolean().default(false),
  unitHasKitchenCabinets: z.boolean().default(false),
  unitHasKitchenSink: z.boolean().default(false),
  unitHasDriversRoom: z.boolean().default(false),
  unitIsSmartHome: z.boolean().default(false),
  unitHasBalcony: z.boolean().default(false),
  unitHasParkingInside: z.boolean().default(false),
  unitHasParkingOutside: z.boolean().default(false),
  unitLegalOwnershipType: z.string().optional(),
  unitOwnershipNumber: z.string().optional(),
  unitOwnershipDate: z.string().optional(),
  unitHasInfoRestriction: z.boolean().default(false),
  unitInfoRestrictionText: z.string().optional(),
  unitHasMortgageRestriction: z.boolean().default(false),
  unitMortgageRestrictionText: z.string().optional(),
  unitHasObligations: z.boolean().default(false),
  unitObligationsText: z.string().optional(),
  unitHasPropertyDispute: z.boolean().default(false),
  unitPropertyDisputeText: z.string().optional(),
  unitOwnershipDocuments: z.array(z.string()).default([]),
  unitOwnerId: z.string().nullable().optional(),
});

export type PropertyFormValues = z.infer<typeof propertyFormSchema>;
